import React, { useState, useEffect } from 'react';
import { Download, Filter, RefreshCw, FileText } from 'lucide-react';
import axios from 'axios';
import ProgressChart from '../../components/SalesTargets/ProgressChart';

const SalesTargetReports = () => {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    periodYear: new Date().getFullYear().toString(),
    periodType: '',
    targetLevel: ''
  });

  useEffect(() => {
    fetchReportData();
  }, [filters]);

  const fetchReportData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.periodYear) params.append('periodYear', filters.periodYear);
      if (filters.periodType) params.append('periodType', filters.periodType);
      if (filters.targetLevel) params.append('targetLevel', filters.targetLevel);

      const response = await axios.get(`/api/salestargets/report/performance?${params.toString()}`);
      setReportData(response.data.report || []);
    } catch (error) {
      console.error('Error fetching report data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleExport = () => {
    // Simple CSV export
    const csv = generateCSV();
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sales-target-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const generateCSV = () => {
    const headers = ['Target Name', 'Level', 'Period', 'Year', 'Overall Progress', 'Status'];
    const rows = reportData.map(item => [
      item.target.TargetName,
      item.target.TargetLevel,
      item.target.PeriodType,
      item.target.PeriodYear,
      `${item.overallProgress}%`,
      item.status
    ]);

    return [headers, ...rows].map(row => row.join(',')).join('\n');
  };

  const getStatusBadge = (status) => {
    const badges = {
      'Achieved': 'bg-green-100 text-green-800',
      'On Track': 'bg-blue-100 text-blue-800',
      'At Risk': 'bg-yellow-100 text-yellow-800',
      'Behind': 'bg-red-100 text-red-800'
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  // Prepare chart data
  const prepareOverallChartData = () => {
    return reportData.map(item => ({
      name: item.target.TargetName.length > 20 
        ? item.target.TargetName.substring(0, 20) + '...' 
        : item.target.TargetName,
      actual: parseFloat(item.overallProgress),
      target: 100
    }));
  };

  const prepareStatusDistribution = () => {
    const distribution = {
      Achieved: 0,
      'On Track': 0,
      'At Risk': 0,
      Behind: 0
    };

    reportData.forEach(item => {
      distribution[item.status] = (distribution[item.status] || 0) + 1;
    });

    return Object.entries(distribution).map(([name, value]) => ({ name, value }));
  };

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <FileText className="w-6 h-6 mr-2" />
              Sales Target Reports
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Analyze target performance and generate reports
            </p>
          </div>
          <button
            onClick={handleExport}
            disabled={reportData.length === 0}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center">
              <Filter className="w-4 h-4 mr-2" />
              Filters
            </h3>
            <button
              onClick={fetchReportData}
              className="text-xs text-blue-600 hover:text-blue-900 flex items-center"
            >
              <RefreshCw className="w-3 h-3 mr-1" />
              Refresh
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Year
              </label>
              <select
                value={filters.periodYear}
                onChange={(e) => handleFilterChange('periodYear', e.target.value)}
                className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm"
              >
                {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Period Type
              </label>
              <select
                value={filters.periodType}
                onChange={(e) => handleFilterChange('periodType', e.target.value)}
                className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm"
              >
                <option value="">All Types</option>
                <option value="Monthly">Monthly</option>
                <option value="Quarterly">Quarterly</option>
                <option value="Annually">Annually</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Target Level
              </label>
              <select
                value={filters.targetLevel}
                onChange={(e) => handleFilterChange('targetLevel', e.target.value)}
                className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm"
              >
                <option value="">All Levels</option>
                <option value="Individual">Individual</option>
                <option value="Team">Team</option>
                <option value="Company">Company</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-2" />
            <p className="text-sm text-gray-600">Loading report data...</p>
          </div>
        </div>
      ) : reportData.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-900">No data available</p>
          <p className="text-xs text-gray-500 mt-1">Create targets and calculate progress to see reports</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ProgressChart
              data={prepareOverallChartData()}
              type="bar"
              title="Target Achievement Overview"
              height={300}
            />
            <ProgressChart
              data={prepareStatusDistribution()}
              type="pie"
              title="Status Distribution"
              height={300}
            />
          </div>

          {/* Detailed Table */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-900">Detailed Performance Report</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Target
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      Level
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      Period
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      Metrics
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      Progress
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {reportData.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {item.target.TargetName}
                          </div>
                          <div className="text-xs text-gray-500">
                            {item.target.TargetEntityName}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center text-sm text-gray-900">
                        {item.target.TargetLevel}
                      </td>
                      <td className="px-6 py-4 text-center text-sm text-gray-900">
                        {item.target.PeriodType} {item.target.PeriodYear}
                      </td>
                      <td className="px-6 py-4 text-center text-sm text-gray-900">
                        {item.metrics.length}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center">
                          <div className="w-24">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs font-medium text-gray-700">
                                {item.overallProgress}%
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className={`h-2 rounded-full ${
                                  item.overallProgress >= 100 ? 'bg-green-500' :
                                  item.overallProgress >= 80 ? 'bg-blue-500' :
                                  item.overallProgress >= 50 ? 'bg-yellow-500' :
                                  'bg-red-500'
                                }`}
                                style={{ width: `${Math.min(item.overallProgress, 100)}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(item.status)}`}>
                          {item.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesTargetReports;


